import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { getSubscriptionStatus } from '@/lib/stripe'
import { parseSubscriptionError, logSubscriptionError } from '@/lib/subscription-errors'

export interface UseUpgradeSuccessReturn {
  showSuccess: boolean
  upgradedPlan: string | null
  isRefreshing: boolean
  error: Error | null
}

/**
 * Hook to handle post-checkout redirect and subscription refresh
 * Detects upgrade success/failure from URL params and updates subscription state
 */
export function useUpgradeSuccess(
  onSuccess?: (plan: string) => void
): UseUpgradeSuccessReturn {
  const [showSuccess, setShowSuccess] = useState(false)
  const [upgradedPlan, setUpgradedPlan] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const handleUpgradeRedirect = async () => {
      const params = new URLSearchParams(window.location.search)
      const upgradeStatus = params.get('upgrade')
      const plan = params.get('plan')
      const sessionId = params.get('session_id')

      // Handle successful upgrade
      if (upgradeStatus === 'success' && plan) {
        setShowSuccess(true)
        setUpgradedPlan(plan)
        setIsRefreshing(true)

        try {
          // Refresh subscription status from backend
          const status = await getSubscriptionStatus()

          if (status && status.planId === plan) {
            toast.success(`Upgrade para o plano ${plan} realizado com sucesso!`, {
              description: 'Seus novos recursos já estão disponíveis.',
              duration: 5000,
            })

            // Call success callback if provided
            onSuccess?.(plan)

            // Optional: Track analytics
            if (typeof window !== 'undefined' && (window as any).gtag) {
              (window as any).gtag('event', 'subscription_upgrade', {
                plan_id: plan,
                session_id: sessionId,
              })
            }
          } else {
            // Subscription doesn't match expected plan
            toast.warning('Processando seu upgrade...', {
              description: 'Pode levar alguns segundos para atualizar.',
            })
          }
        } catch (err) {
          const subscriptionError = parseSubscriptionError(err)
          setError(subscriptionError)
          logSubscriptionError(subscriptionError, { plan, sessionId })

          toast.error('Erro ao atualizar status da assinatura', {
            description: 'Por favor, recarregue a página em alguns instantes.',
          })
        } finally {
          setIsRefreshing(false)
        }

        // Clean up URL params
        window.history.replaceState({}, '', window.location.pathname)
      }
      // Handle canceled checkout
      else if (upgradeStatus === 'canceled') {
        toast.info('Checkout cancelado', {
          description: 'Você pode tentar novamente quando quiser.',
        })

        // Clean up URL params
        window.history.replaceState({}, '', window.location.pathname)
      }
      // Handle failed payment
      else if (upgradeStatus === 'failed') {
        toast.error('Pagamento falhou', {
          description: 'Verifique seus dados de pagamento e tente novamente.',
        })

        // Clean up URL params
        window.history.replaceState({}, '', window.location.pathname)
      }
    }

    handleUpgradeRedirect()
  }, [onSuccess])

  return {
    showSuccess,
    upgradedPlan,
    isRefreshing,
    error
  }
}
